// import styled from "@emotion/styled";
import styled from "@emotion/styled";
import { LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import React, { useRef } from "react";
import * as Ons from "react-onsenui";
import { API } from "../../API";
import { isLoading, Loading } from "../../Loading";
import {
  PatientDocumentTypeId,
  PlanOfCareItemId,
  VisitInstanceDetails,
  VisitInstanceId,
} from "../../messages";
import {
  RequiredDutySheetTask,
  SelectedDutySheetTask,
  VisitInstanceDutySheet,
} from "../../messages/DutySheet";
import { STRINGS } from "../../strings";
import { globalize, translate } from "../../translate";
import { HashMap } from "../../utils/HashMap";
import { List } from "../../utils/List";
import { CHECK_ICON, CHEVRON_DOWN_ICON_THIN } from "../../utils/svgs_new";
import ClockInOutModals, { useClockInOutModals } from "../ClockInOutModals";
import DutySheetActivity from "../DutySheetActivity";
import DutySheetCompletedModal from "../DutySheetCompletedModal/DutySheetVisitCompletedModal";
import { AppBottomSheet } from "../Generic/AppBottomSheet";
import { AppButton } from "../Generic/AppButton";
import { AppIcon } from "../Generic/AppIcon";
import { AppOverlay } from "../Generic/AppOverlay";
import Flex from "../Generic/Flex";
import { If } from "../Generic/If";
import { isDefined } from "../Generic/IsDefined";
import QuestionErrorBottomSheet from "../QuestionErrorBottomSheet";
import SuccessfulVisitInstanceModal from "../SuccessfulVisitInstanceModal";
import { SelectedDutyTasks } from "../Timesheet/useTimesheetForm";
import { UseContactSupportReturnType } from "../useContactSupport";
import VisitInstanceScreenFooter from "../VisitInstanceScreenFooter";

interface UseVisitLetsStartBottomSheetProps {
  activeVisit: VisitInstanceDetails;
  promptClockInMapModal: () => void;
  promptClockOutMapModal: () => void;
  postPatientDocAnswer: API["postPatientDocAnswer"];
  postSubmitPatientDocument: API["postSubmitPatientDocument"];
  isOpen: boolean;
  onHideOrShowVisitBottomSheet: (isOpen: boolean) => void;
  initialDutySheetData: VisitInstanceDutySheet;
  onOpenChangesSavedBanner: () => void;
  onSubmitDutySheet: (visitInstanceId: VisitInstanceId) => void;
}

export interface VisitLetsStartBottomSheetProps {
  isOpen: boolean;
  visitDate: LocalDate;
  patientName: string;
  visitStartTime: LocalTime;
  visitEndTime: LocalTime;
  clockInToleranceMinutes: number;
  clockOutToleranceMinutes: number;
  requiredDutyTasks: List<RequiredDutySheetTask>;
  canClockIn: boolean;
  canClockOut: boolean;
  canSubmitTimesheet: boolean;
  clockInOutModalsProps: ReturnType<typeof useClockInOutModals>["state"];
  selectedDutyTasks: SelectedDutyTasks;
  isDutySheetCompleted: boolean;
  hasPlanOfCare: boolean;
  isErrorModalOpen: boolean;
  contactSupport: UseContactSupportReturnType | Loading;
  errorMessage: string | null;
  clockInTime: LocalDateTime | null;
  clockOutTime: LocalDateTime | null;
  isSubmitting: boolean;
  isDutySheetCompletedModalOpen: boolean;
  visitInstanceId: VisitInstanceId;
  onCheckTask: (taskId: PlanOfCareItemId, isChecked: boolean) => void;
  onChangeNotes: (taskId: PlanOfCareItemId, notes: string | null) => void;
  onClickClockIn: () => void;
  onClickClockOut: () => void;
  onDutySheetCompletedModalClose: () => void;
  onRequestOpenErrorModal: (errorMessage: string) => void;
  onRequestCloseErrorModal: () => void;
  onSubmit: () => void;
  onOpenChangesSavedBanner: () => void;
  onSubmitTimesheet: () => void;
  onRequestClose: () => void;
  postPatientDocAnswer: API["postPatientDocAnswer"];
  postSubmitPatientDocument: API["postSubmitPatientDocument"];
}

const checkOverflow = (container: HTMLDivElement | null): boolean => {
  if (isDefined(container)) {
    return container.clientHeight < container.scrollHeight;
  }
  return false;
};

export function useVisitLetsStartBottomSheet(props: UseVisitLetsStartBottomSheetProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isDutySheetCompletedModalOpen, setIsDutySheetCompletedModalOpen] = React.useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const dutySheetDocument = PatientDocumentTypeId.wrap(-12345); // TODO: Update to new dutysheet

  const visitStartTime = props.activeVisit.startTimeLocal.toLocalTime();

  const visitEndTime = props.activeVisit.endTimeLocal.toLocalTime();

  const clockInToleranceMinutes = props.activeVisit.publicVisitDetails.clockInToleranceMinutes;

  const clockOutToleranceMinutes = props.activeVisit.publicVisitDetails.clockOutToleranceMinutes;

  const requiredDutyTasks = List.fromArray(props.initialDutySheetData.requiredDutyTasks);

  const [selectedDutyTasks, setSelectedDutyTasks] = React.useState<SelectedDutyTasks>(
    HashMap.fromArray(
      props.initialDutySheetData.selectedDuties.map((x: SelectedDutySheetTask) => [
        x.planOfCareItemId,
        { notes: x.notes },
      ])
    )
  );

  const handleErrorModalOpen = (error: string) => {
    setErrorMessage(error);
    setIsErrorModalOpen(true);
  };

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const onRequestOpen = () => {
    props.onHideOrShowVisitBottomSheet(true);
  };

  const onRequestClose = () => {
    props.onHideOrShowVisitBottomSheet(false);
  };

  const onClickClockIn = () => {
    props.onHideOrShowVisitBottomSheet(false);
    props.promptClockInMapModal();
  };

  const onClickClockOut = () => {
    props.onHideOrShowVisitBottomSheet(false);
    props.promptClockOutMapModal();
  };

  const handleDutySheetCompletedModalClose = () => {
    setIsDutySheetCompletedModalOpen(false);
    props.onHideOrShowVisitBottomSheet(true);
  };

  const handleCheckTask = (id: PlanOfCareItemId, isChecked: boolean) => {
    setSelectedDutyTasks((prev) => (isChecked ? prev.set(id, { notes: null }) : prev.delete(id)));
  };

  const handleChangeNotes = (id: PlanOfCareItemId, notes: string | null) => {
    setSelectedDutyTasks((prev) =>
      prev.set(id, { notes: isDefined(notes) && notes !== "" ? notes : null })
    );
  };

  function handleSubmit() {
    if (props.activeVisit.isDutySheetCompleted) {
      handleErrorModalOpen(translate(STRINGS.UNABLE_TO_EDIT_SUBMITTED_DUTY_SHEET));
      return;
    }

    const requiredDuties = requiredDutyTasks
      .filter((task) => task.isRequired)
      .map((task) => task.id);
    const selectedDuties = Array.from(selectedDutyTasks.keys());
    if (requiredDuties.some((id) => !selectedDuties.includes(id))) {
      handleErrorModalOpen(translate(STRINGS.REQUIRED_FIELDS_MISSING_IN_PATIENT_DOC));
      return;
    }

    setIsSubmitting(true);

    props
      .postSubmitPatientDocument(props.activeVisit.id, dutySheetDocument, {
        submissionDate: LocalDate.now(),
      })
      .then(() => {
        setIsSubmitting(false);
        openDutySheetSubmittedModal();
        props.onSubmitDutySheet(props.activeVisit.id);
      });
  }

  const openDutySheetSubmittedModal = () => {
    props.onHideOrShowVisitBottomSheet(false);
    setIsDutySheetCompletedModalOpen(true);
  };

  return {
    bottomSheetProps: {
      clockInTime: props.activeVisit.clockInTime,
      clockOutTime: props.activeVisit.clockOutTime,
      clockInToleranceMinutes: clockInToleranceMinutes,
      clockOutToleranceMinutes: clockOutToleranceMinutes,
      canSubmitTimesheet: false,
      visitDate: props.activeVisit.startTimeLocal.toLocalDate(),
      patientName: props.activeVisit.confidentialVisitDetails.patientName,
      errorMessage: errorMessage,
      isDutySheetCompletedModalOpen: isDutySheetCompletedModalOpen,
      isDutySheetCompleted: props.activeVisit.isDutySheetCompleted,
      hasPlanOfCare: props.activeVisit.hasPlanOfCare,
      isErrorModalOpen: isErrorModalOpen,
      isOpen: props.isOpen,
      isSubmitting: isSubmitting,
      requiredDutyTasks: requiredDutyTasks,
      selectedDutyTasks: selectedDutyTasks,
      visitStartTime: visitStartTime,
      visitEndTime: visitEndTime,
      visitInstanceId: props.activeVisit.id,
      onClickClockIn: onClickClockIn,
      onClickClockOut: onClickClockOut,
      onCheckTask: handleCheckTask,
      onDutySheetCompletedModalClose: handleDutySheetCompletedModalClose,
      onOpenChangesSavedBanner: props.onOpenChangesSavedBanner,
      onRequestClose: onRequestClose,
      onRequestOpenVisitLetsStartBottomSheet: onRequestOpen,
      onRequestOpenErrorModal: handleErrorModalOpen,
      onRequestCloseErrorModal: handleErrorModalClose,
      onChangeNotes: handleChangeNotes,
      onSubmit: handleSubmit,
      onSubmitTimesheet: () => null,
      postPatientDocAnswer: props.postPatientDocAnswer,
      postSubmitPatientDocument: props.postSubmitPatientDocument,
    },
  };
}

const getTitleText = (
  clockInTime: LocalDateTime | null,
  clockOutTime: LocalDateTime | null,
  isDutySheetCompleted: boolean,
  hasPlanOfCare: boolean
) => {
  if (clockInTime !== null && !isDutySheetCompleted && hasPlanOfCare) {
    return translate(STRINGS.DONT_FORGET_TO_FILL_THE_DUTY_SHEET);
  }

  if (clockInTime !== null && clockOutTime !== null) {
    return translate(STRINGS.VISIT_COMPLETE);
  }

  return translate(STRINGS.LETS_START);
};

function VisitLetsStartBottomSheet(props: VisitLetsStartBottomSheetProps) {
  const visitHoursText = `${props.visitStartTime.toString()} - ${props.visitEndTime.toString()}`;
  const tasks = useRef<HTMLDivElement | null>(null);
  const [areTasksOverflown, setTasksOverflown] = React.useState(false);
  const [isScrolledToBottom, setScrollToBottom] = React.useState(false);

  React.useEffect(() => {
    setTasksOverflown(checkOverflow(tasks.current));
  }, [tasks]);

  const scrollToBottomOfTasks = () => {
    if (!areTasksOverflown) {
      return;
    }
    if (isDefined(tasks) && isDefined(tasks.current)) {
      tasks.current.scrollTop = tasks.current.scrollHeight - tasks.current.offsetHeight;
      setScrollToBottom(true);
    }
  };

  const onTasksScroll = () => {
    if (!areTasksOverflown) {
      return;
    }
    if (isDefined(tasks) && isDefined(tasks.current)) {
      setScrollToBottom(
        tasks.current.scrollTop === tasks.current.scrollHeight - tasks.current.offsetHeight
      );
    }
  };

  return (
    <AppOverlay>
      <VisitLetsStartBottomSheet.Root
        isOpen={props.isOpen}
        onRequestClose={props.onRequestClose}
        rounded={true}
      >
        <VisitLetsStartBottomSheet.Header>
          <span>
            {getTitleText(
              props.clockInTime,
              props.clockOutTime,
              props.isDutySheetCompleted,
              props.hasPlanOfCare
            )}
          </span>
          <If condition={props.clockInTime === null && props.clockOutTime === null}>
            <VisitLetsStartBottomSheet.SubHeader>
              <VisitLetsStartBottomSheet.SubHeaderText>
                {globalize().formatLocalDate(props.visitDate)}
              </VisitLetsStartBottomSheet.SubHeaderText>
              <VisitLetsStartBottomSheet.SubHeaderText>
                {props.patientName}
              </VisitLetsStartBottomSheet.SubHeaderText>
              <VisitLetsStartBottomSheet.SubHeaderText>
                {visitHoursText}
              </VisitLetsStartBottomSheet.SubHeaderText>
            </VisitLetsStartBottomSheet.SubHeader>
          </If>
        </VisitLetsStartBottomSheet.Header>

        <If condition={props.clockInTime === null || props.clockOutTime === null}>
          <VisitLetsStartBottomSheet.ClockInOutFooter>
            <VisitInstanceScreenFooter
              canClockIn={props.canClockIn}
              canClockOut={props.canClockOut}
              canSubmitTimesheet={props.canSubmitTimesheet}
              onClockIn={props.onClickClockIn}
              onClockOut={props.onClickClockOut}
              onSubmitTimesheet={props.onSubmitTimesheet}
              clockInDateTime={props.clockInTime}
            />
            <VisitLetsStartBottomSheet.ClockInOutHelperText>
              <Flex direction="row" xGap={8} justifyContent="space-around">
                <Flex direction="column" alignItems="center">
                  <span>
                    {translate(
                      props.canClockIn ? STRINGS.CLOCK_IN_BETWEEN : STRINGS.SUCCESSFULLY_CLOCKED_IN
                    )}{" "}
                  </span>
                  <span>{props.canClockIn ? visitHoursText : null}</span>
                </Flex>
                <Flex direction="column" alignItems="center">
                  <span>
                    {translate(
                      props.canClockOut || props.visitEndTime.isAfter(LocalTime.now())
                        ? STRINGS.CLOCK_OUT_BETWEEN
                        : STRINGS.SUCCESSFULLY_CLOCKED_OUT
                    )}
                  </span>
                  <span>
                    {props.canClockOut || props.visitEndTime.isAfter(LocalTime.now())
                      ? visitHoursText
                      : null}
                  </span>
                </Flex>
              </Flex>
            </VisitLetsStartBottomSheet.ClockInOutHelperText>
          </VisitLetsStartBottomSheet.ClockInOutFooter>
        </If>

        <If condition={isDefined(props.clockInTime) && isDefined(props.clockOutTime)}>
          <SuccessfulVisitInstanceModal
            clockInDateTime={props.clockInTime}
            clockOutDateTime={props.clockOutTime}
          />
        </If>

        <If condition={props.requiredDutyTasks.length() > 0}>
          <VisitLetsStartBottomSheet.TasksHeader>
            <span>{translate(STRINGS.TASKS)}</span>
            <If condition={props.isDutySheetCompleted}>
              <AppIcon icon={CHECK_ICON} />
            </If>
          </VisitLetsStartBottomSheet.TasksHeader>
        </If>

        <VisitLetsStartBottomSheet.Tasks
          ref={tasks}
          onScroll={onTasksScroll}
          areTasksOverflown={areTasksOverflown}
        >
          <DutySheetActivity
            visitInstanceId={props.visitInstanceId}
            isDutySheetCompleted={props.isDutySheetCompleted}
            onChangeNotes={props.onChangeNotes}
            onCheckTask={props.onCheckTask}
            onRequestOpenErrorModal={props.onRequestOpenErrorModal}
            requiredDutyTasks={props.requiredDutyTasks}
            selectedDutyTasks={props.selectedDutyTasks}
            postPatientDocAnswer={props.postPatientDocAnswer}
            onOpenChangesSavedBanner={props.onOpenChangesSavedBanner}
          />
        </VisitLetsStartBottomSheet.Tasks>

        <VisitLetsStartBottomSheet.ScrollToBottom
          isDutySheetCompleted={props.isDutySheetCompleted}
          onClick={scrollToBottomOfTasks}
        >
          <VisitLetsStartBottomSheet.ScrollDownIcon
            hidden={!areTasksOverflown || isScrolledToBottom}
          >
            <AppIcon icon={CHEVRON_DOWN_ICON_THIN} size={20} style={{ width: 20 }} fill="white" />
          </VisitLetsStartBottomSheet.ScrollDownIcon>
        </VisitLetsStartBottomSheet.ScrollToBottom>

        <If condition={!props.isDutySheetCompleted && props.requiredDutyTasks.length() > 0}>
          <VisitLetsStartBottomSheet.Footer>
            <If condition={!props.isSubmitting}>
              <AppButton
                variant="filled"
                onClick={props.onSubmit}
                disabled={props.selectedDutyTasks.size() === 0}
              >
                <VisitLetsStartBottomSheet.SubmitButton>
                  <AppIcon icon={CHECK_ICON} />
                  {translate(STRINGS.SUBMIT_DUTIES)}
                </VisitLetsStartBottomSheet.SubmitButton>
              </AppButton>
            </If>
            <If condition={props.isSubmitting}>
              <VisitLetsStartBottomSheet.Loading>
                <Ons.ProgressCircular indeterminate={true} />
              </VisitLetsStartBottomSheet.Loading>
            </If>
          </VisitLetsStartBottomSheet.Footer>
        </If>
      </VisitLetsStartBottomSheet.Root>
      <DutySheetCompletedModal
        isOpen={props.isDutySheetCompletedModalOpen}
        onRequestClose={props.onDutySheetCompletedModalClose}
      />

      {isLoading(props.contactSupport) ? null : (
        <AppOverlay>
          <ClockInOutModals
            {...props.clockInOutModalsProps}
            contactSupportProps={props.contactSupport}
          />
        </AppOverlay>
      )}

      <QuestionErrorBottomSheet
        isOpen={props.isErrorModalOpen}
        title={translate(STRINGS.OOPS)}
        errorMessage={props.errorMessage}
        onRequestClose={props.onRequestCloseErrorModal}
      />
    </AppOverlay>
  );
}

VisitLetsStartBottomSheet.Root = styled(AppBottomSheet)`
  .AppBottomSheet_Container {
    color: #3d3d3d;
    max-height: 85vh;
    overflow: hidden;

    .AppBottomSheet_Content {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }
`;

VisitLetsStartBottomSheet.Footer = styled.div`
  position: sticky;
  background-color: var(--scaffold-header-background);
  bottom: 0;
  padding: 12px 0 12px 0;
  box-shadow: 0px -4px 5px -6px #333;
`;

VisitLetsStartBottomSheet.ClockInOutHelperText = styled.div`
  text-align: center;
  margin-top: 5px;
`;

VisitLetsStartBottomSheet.Header = styled.div`
  font-weight: 800;
  font-size: 20px;
  line-height: 24px;
  display: flex;
  flex-direction: column;
`;

VisitLetsStartBottomSheet.SubHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

VisitLetsStartBottomSheet.SubHeaderText = styled.div`
  font-weight: 600;
  font-size: 18px;
`;

VisitLetsStartBottomSheet.Tasks = styled.div<{ areTasksOverflown: boolean }>`
  display: flex;
  flex-direction: row;
  max-height: 50vh;
  overflow: auto;
  margin-inline-end: ${(p) => (p.areTasksOverflown ? 36 : 0)}px;
  scroll-behavior: smooth;
`;

VisitLetsStartBottomSheet.TasksHeader = styled.div`
  color: #3d3d3d;
  font-weight: 800;
  font-size: 16px;
  line-height: 22px;
  margin-top: 8px;
  margin-bottom: 4px;
  display: flex;
  flex-direction: row;
  text-transform: capitalize;
  gap: 7px;
`;

VisitLetsStartBottomSheet.SubmitButton = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

VisitLetsStartBottomSheet.ClockInOutFooter = styled.div`
  .ClockInOutFooter {
    box-shadow: none;
    padding: 0;
  }
`;

VisitLetsStartBottomSheet.Loading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

VisitLetsStartBottomSheet.ScrollDownIcon = styled.div`
  padding: 8px;
  border-radius: 20px;
  background: var(--primary-color);
  height: 20px;
  animation: pulse 2s ease infinite;
`;

VisitLetsStartBottomSheet.ScrollToBottom = styled.div<{ isDutySheetCompleted: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  bottom: ${(p) => (p.isDutySheetCompleted ? 40 : 100)}px;
  right: 10px;
`;

export default VisitLetsStartBottomSheet;
